import { type TodoItem, SidebarBoxKey } from 'AdmissionsGuidance/StudentDashboard.types';
import { useAngularContext } from 'AngularContext';
import { useCurrentUser, useTargetBrandConfig } from 'FrontRoyalAngular';
import { getEnrollmentSidebarTodoGroups } from 'Lessons';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCohort } from 'Users';
import { faFileLines } from '@fortawesome/pro-regular-svg-icons/faFileLines';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { Text } from 'Text';
import clsx from 'clsx';
import { BlockLink } from 'BlockLink';
import { ProgramTypeConfigs } from 'Program';
import { InlineLink } from 'InlineLink';
import { BasicMobileBox } from '../../BasicMobileBox';
import { ImportantCtaBox } from '../../ImportantCtaBox';
import { enrollmentTodoGroupVisible, enrollmentTodoVisible, hasCompleteItem as hasCompleteItemHelper } from './helpers';
import { EnrollmentTodoItem } from './EnrollmentTodoItem';

const PAST_DUE = 'pastDue';
const CALENDLY_APPOINTMENT_URL = 'https://calendly.com/d/d7n-s3n-fx4';

function EnrollmentBoxComponent({ mobile }: { mobile?: boolean }) {
    const $injector = useAngularContext();
    const currentUser = useCurrentUser();
    const cohort = useMemo(() => getCohort(currentUser), [currentUser]);
    const brandConfig = useTargetBrandConfig();
    const icon = useMemo(() => brandConfig.enrollmentBoxIcon, [brandConfig.enrollmentBoxIcon]);
    const programTypeConfig = useMemo(
        () => (cohort?.programType ? ProgramTypeConfigs[cohort.programType] : null),
        [cohort?.programType],
    );
    const enrollmentFaqUrl = useMemo(
        () => programTypeConfig?.enrollmentFaqLink ?? null,
        [programTypeConfig?.enrollmentFaqLink],
    );
    const registrarSupportEmailAddress = useMemo(() => {
        const supportEmailDomain = programTypeConfig?.supportEmailDomain;
        return supportEmailDomain ? `registrar@${supportEmailDomain}` : null;
    }, [programTypeConfig?.supportEmailDomain]);
    const enrollmentTodoGroups = useMemo(() => getEnrollmentSidebarTodoGroups($injector), [$injector]);
    const [_todos, todosByDate] = enrollmentTodoGroups;
    const hasCompleteItem = useMemo(
        () => hasCompleteItemHelper((Object.values(todosByDate) as TodoItem[][]).flat()),
        [todosByDate],
    );

    return mobile ? (
        <EnrollmentBoxMobilePresentation
            todosByDate={todosByDate}
            hasCompleteItem={hasCompleteItem}
            enrollmentFaqUrl={enrollmentFaqUrl}
            registrarSupportEmailAddress={registrarSupportEmailAddress}
            icon={icon}
        />
    ) : (
        <EnrollmentBoxPresentation
            todosByDate={todosByDate}
            hasCompleteItem={hasCompleteItem}
            enrollmentFaqUrl={enrollmentFaqUrl}
            registrarSupportEmailAddress={registrarSupportEmailAddress}
            icon={icon}
        />
    );
}

function EnrollmentBoxPresentation({
    todosByDate,
    hasCompleteItem,
    enrollmentFaqUrl,
    registrarSupportEmailAddress,
    icon,
}: {
    todosByDate: { [dueDate: string]: TodoItem[] };
    hasCompleteItem: boolean;
    enrollmentFaqUrl: string | null;
    registrarSupportEmailAddress: string | null;
    icon: string | null;
}) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'enrollmentBox' });
    const [showCompletedTodos, setShowCompletedTodos] = useState(false);
    return (
        <ImportantCtaBox
            headerSubtitle={t('complete_your')}
            headerTitle={t('enrollment')}
            boxKey={SidebarBoxKey.enrollment}
            color="yellow"
            icon={icon ? <img src={icon} alt="Enrollment Box Icon" /> : <FontAwesomeIcon icon={faFileLines} />}
            iconCircle={!icon}
        >
            <div className="pt-2">
                {Object.keys(todosByDate).map((dueDate: string) => {
                    const todoItems = todosByDate[dueDate].filter(item =>
                        enrollmentTodoVisible(item, showCompletedTodos),
                    );
                    return (
                        <div key={dueDate} className="mb-5">
                            {dueDate === PAST_DUE && todoItems.length > 0 && (
                                <Text
                                    as="div"
                                    weight="semibold"
                                    color="black"
                                    className="mb-2 flex items-center justify-start text-base"
                                >
                                    {t('past_due')}
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="mx-1" />
                                </Text>
                            )}
                            {dueDate !== PAST_DUE && enrollmentTodoGroupVisible(todoItems, showCompletedTodos) && (
                                <Text
                                    as="div"
                                    weight="semibold"
                                    color="black"
                                    className="mb-2 flex items-center justify-start text-base"
                                >
                                    {t('due_by')} {dueDate}
                                </Text>
                            )}
                            {todoItems.map(item => (
                                <EnrollmentTodoItem
                                    key={item.name}
                                    item={item}
                                    showCompletedTodos={showCompletedTodos}
                                />
                            ))}
                        </div>
                    );
                })}
            </div>
            {hasCompleteItem && (
                <div className="flex items-center justify-center">
                    <BlockLink
                        onPress={() => setShowCompletedTodos(prev => !prev)}
                        color="slate-grey"
                        className={clsx('text-base', { 'mb-5': enrollmentFaqUrl })}
                        anchorIcon={showCompletedTodos ? 'chevron-up' : 'chevron-down'}
                    >
                        {t(showCompletedTodos ? 'hide_completed_steps' : 'show_completed_steps')}
                    </BlockLink>
                </div>
            )}
            {enrollmentFaqUrl && registrarSupportEmailAddress && (
                <Text as="div" color="black" className="text-base">
                    {t('footer_note', {
                        enrollmentFaqLink: (
                            <InlineLink href={enrollmentFaqUrl} target="_blank">
                                {t('enrollment_requirements')}
                            </InlineLink>
                        ),
                        registrarLink: (
                            <InlineLink href={`mailto:${registrarSupportEmailAddress}`} target="_blank">
                                {registrarSupportEmailAddress}
                            </InlineLink>
                        ),
                        appointmentLink: (
                            <InlineLink href={CALENDLY_APPOINTMENT_URL} target="_blank">
                                {t('appointment')}
                            </InlineLink>
                        ),
                    })}
                </Text>
            )}
        </ImportantCtaBox>
    );
}

function EnrollmentBoxMobilePresentation({
    todosByDate,
    hasCompleteItem,
    enrollmentFaqUrl,
    registrarSupportEmailAddress,
    icon,
}: {
    todosByDate: { [dueDate: string]: TodoItem[] };
    hasCompleteItem: boolean;
    enrollmentFaqUrl: string | null;
    registrarSupportEmailAddress: string | null;
    icon: string | null;
}) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'enrollmentBox' });
    return (
        <BasicMobileBox
            heading={t('mobile_heading')}
            ctaText={t('mobile_cta_text')}
            boxKey={SidebarBoxKey.enrollment}
            expandedContent={
                <EnrollmentBoxPresentation
                    todosByDate={todosByDate}
                    hasCompleteItem={hasCompleteItem}
                    enrollmentFaqUrl={enrollmentFaqUrl}
                    registrarSupportEmailAddress={registrarSupportEmailAddress}
                    icon={icon}
                />
            }
        />
    );
}

export const EnrollmentBox = memo(EnrollmentBoxComponent) as typeof EnrollmentBoxComponent;
